/**
 * Wallet, top-up and referral admin API client.
 *
 * House pattern (see `b2bApi.ts`): the shared axios instance, so every call
 * inherits the auth header, the request id, the 401 logout and the error toast.
 * Functions return the UNWRAPPED payload rather than the `{ success, data }`
 * envelope.
 *
 * Every read here is derived server-side FROM THE LEDGER, not from a cached
 * balance - the one exception is `outstanding_liability`, which is the point-in-
 * time sum of account rows and is cross-checked against the ledger by
 * `getReconciliation`.
 */
import api, { unwrap } from './api'

/** The date chips every report shares. */
export type DateRangePreset = 'today' | 'yesterday' | 'last_7_days' | 'last_30_days' | 'custom'

export interface DateRangeFilter {
  range?: DateRangePreset
  from?: string
  to?: string
}

export interface WalletSummary {
  range: { from: string | null; to: string | null; preset: string }
  outstanding_liability: number
  customer_funded_balance: number
  promotional_balance: number
  wallets_with_balance: number
  total_wallets: number
  total_credits: number
  total_debits: number
  net_change: number
  transaction_count: number
  active_users: number
  credits_breakdown: {
    topup: number
    topup_bonus: number
    referral_rewards: number
    promotional_credits: number
    refunds: number
    admin_credits: number
  }
  debits_breakdown: {
    order_payments: number
    admin_debits: number
    reward_reversals: number
  }
  topups: {
    revenue_collected: number
    promotional_bonus_issued: number
    successful: number
    failed: number
    pending: number
  }
}

export interface WalletTransactionRecord {
  id: string
  txn_ref: string
  user_id: string
  direction: 'credit' | 'debit'
  type: string
  amount: string
  cash_delta: string
  promo_delta: string
  balance_before: string
  balance_after: string
  source: string
  reference_type: string | null
  reference_id: string | null
  status: 'completed' | 'reversed'
  created_at: string
  customer_name: string | null
  customer_phone: string | null
  adjusted_by_name: string | null
}

export interface WalletCustomerRecord {
  user_id: string
  name: string | null
  phone: string
  referral_code: string | null
  balance: number
  cash_balance: number
  promo_balance: number
  status: 'active' | 'frozen'
  updated_at: string
}

export interface ReconciliationReport {
  range: { from: string | null; to: string | null; preset: string }
  opening_liability: number
  credits: number
  debits: number
  closing_liability: number
  live_liability: number
  breakdown: {
    customer_funded_credits: number
    promotional_credits: number
    referral_rewards: number
    refund_credits: number
    admin_credits: number
    order_spend: number
  }
  gateway_reconciliation: {
    topup_count: number
    gateway_collected: number
    bonus_issued: number
    wallet_credited: number
    variance: number
  }
  integrity: {
    accounts_checked: number
    healthy: boolean
    mismatches: Array<{
      user_id: string
      cached_balance: number | null
      ledger_balance: number
    }>
  }
  balanced: boolean
}

export interface TopupRecord {
  id: string
  user_id: string
  offer_code: string | null
  pay_amount: string
  credit_amount: string
  bonus_amount: string
  status: 'created' | 'paid' | 'failed'
  razorpay_order_id: string | null
  razorpay_payment_id: string | null
  failure_reason: string | null
  paid_at: string | null
  created_at: string
  customer_name: string | null
  customer_phone: string | null
}

export interface ReferralOverview {
  range: { from: string | null; to: string | null; preset: string }
  total_referrals: number
  successful_referrals: number
  pending_referrals: number
  rejected_referrals: number
  reversed_referrals: number
  conversion_rate: number
  rewards_issued: number
  payout_count: number
  top_referrers: Array<{
    id: string
    name: string | null
    phone: string
    referral_code: string | null
    referrals: number
    successful: number
    earned: number
  }>
}

export interface TopupOfferRecord {
  id: string
  code: string
  name: string
  description: string | null
  pay_amount: string
  credit_amount: string
  bonus_amount: string
  starts_at: string | null
  ends_at: string | null
  max_redemptions: number | null
  max_per_user: number | null
  total_redemptions: number
  is_active: boolean
  sort_order: number
}

export interface ReferralCampaignRecord {
  id: string
  name: string
  referrer_reward_amount: string
  referee_reward_amount: string
  reward_kind: 'wallet_credit' | 'promo_credit'
  qualifying_event: 'FIRST_COMPLETED_ORDER' | 'FIRST_PAID_ORDER'
  referee_reward_timing: 'qualification' | 'signup'
  min_order_amount: string
  max_rewards_per_referrer: number | null
  starts_at: string | null
  ends_at: string | null
  is_active: boolean
}

const BASE = '/admin/wallet'

/** Drop empty filter keys so `?range=&from=` never reaches the server. */
const params = (filter: object = {}) =>
  Object.fromEntries(Object.entries(filter).filter(([, v]) => v !== undefined && v !== '' && v !== null))

export const getWalletSummary = async (filter: DateRangeFilter = {}): Promise<WalletSummary> => {
  const res = await api.get(`${BASE}/summary`, { params: params(filter) })
  return unwrap(res.data) as WalletSummary
}

export const getWalletTransactions = async (
  filter: DateRangeFilter & {
    limit?: number
    offset?: number
    type?: string
    direction?: string
    user_id?: string
    txn_ref?: string
  } = {},
): Promise<{
  transactions: WalletTransactionRecord[]
  total: number
  filtered_credits: number
  filtered_debits: number
}> => {
  const res = await api.get(`${BASE}/transactions`, { params: params(filter) })
  return unwrap(res.data) as never
}

export const getWalletCustomers = async (
  filter: { limit?: number; offset?: number; search?: string; with_balance?: boolean } = {},
): Promise<{ customers: WalletCustomerRecord[]; total: number }> => {
  const res = await api.get(`${BASE}/customers`, { params: params(filter) })
  return unwrap(res.data) as never
}

export const getReconciliation = async (filter: DateRangeFilter = {}): Promise<ReconciliationReport> => {
  const res = await api.get(`${BASE}/reconciliation`, { params: params(filter) })
  return unwrap(res.data) as ReconciliationReport
}

/**
 * The only human-driven balance change in the system.
 *
 * A reason is mandatory server-side, and the shared axios instance attaches an
 * Idempotency-Key - which this route's backend allowlist entry honours, so a
 * double-submitted form credits once.
 */
export const adjustWallet = async (payload: {
  user_id: string
  amount: number
  direction: 'credit' | 'debit'
  reason: string
}): Promise<{ transaction: WalletTransactionRecord; balance: number }> => {
  const res = await api.post(`${BASE}/adjust`, payload)
  return unwrap(res.data) as never
}

export const getTopups = async (
  filter: DateRangeFilter & { limit?: number; offset?: number; status?: string } = {},
): Promise<{
  topups: TopupRecord[]
  total: number
  totals: {
    revenue_collected: number
    promotional_bonus_issued: number
    wallet_credited: number
    successful: number
    failed: number
  }
}> => {
  const res = await api.get('/admin/topups', { params: params(filter) })
  return unwrap(res.data) as never
}

export const getReferralOverview = async (filter: DateRangeFilter = {}): Promise<ReferralOverview> => {
  const res = await api.get('/admin/referrals/overview', { params: params(filter) })
  return unwrap(res.data) as ReferralOverview
}

export const listTopupOffers = async (): Promise<TopupOfferRecord[]> => {
  const res = await api.get(`${BASE}/offers`)
  return (unwrap(res.data) as { offers: TopupOfferRecord[] }).offers
}

export const createTopupOffer = async (
  payload: Partial<TopupOfferRecord> & { code: string; name: string; pay_amount: number; credit_amount: number },
): Promise<TopupOfferRecord> => {
  const res = await api.post(`${BASE}/offers`, payload)
  return (unwrap(res.data) as { offer: TopupOfferRecord }).offer
}

export const updateTopupOffer = async (
  id: string,
  payload: Partial<TopupOfferRecord>,
): Promise<TopupOfferRecord> => {
  const res = await api.patch(`${BASE}/offers/${id}`, payload)
  return (unwrap(res.data) as { offer: TopupOfferRecord }).offer
}

export const listReferralCampaigns = async (): Promise<ReferralCampaignRecord[]> => {
  const res = await api.get('/admin/referrals/campaigns')
  return (unwrap(res.data) as { campaigns: ReferralCampaignRecord[] }).campaigns
}

export const createReferralCampaign = async (
  payload: Partial<ReferralCampaignRecord> & { name: string },
): Promise<ReferralCampaignRecord> => {
  const res = await api.post('/admin/referrals/campaigns', payload)
  return (unwrap(res.data) as { campaign: ReferralCampaignRecord }).campaign
}

export const updateReferralCampaign = async (
  id: string,
  payload: Partial<ReferralCampaignRecord>,
): Promise<ReferralCampaignRecord> => {
  const res = await api.patch(`/admin/referrals/campaigns/${id}`, payload)
  return (unwrap(res.data) as { campaign: ReferralCampaignRecord }).campaign
}

/** ₹1,23,456.78 - Indian grouping, two decimals only when there are paise. */
export const formatINR = (value: number | string): string => {
  const amount = Number(value || 0)
  const hasPaise = Math.round(amount * 100) % 100 !== 0
  return `₹${amount.toLocaleString('en-IN', {
    minimumFractionDigits: hasPaise ? 2 : 0,
    maximumFractionDigits: 2,
  })}`
}

/** Ledger type → the label an operator should read. */
export const TRANSACTION_TYPE_LABELS: Record<string, string> = {
  topup: 'Top-up (customer paid)',
  topup_bonus: 'Top-up bonus (promotional)',
  referral_reward: 'Referral reward',
  referral_signup_bonus: 'Referral welcome bonus',
  promo_credit: 'Promotional credit',
  refund: 'Refund to wallet',
  order_payment: 'Order payment',
  admin_credit: 'Manual credit',
  admin_debit: 'Manual debit',
  reward_reversal: 'Reward clawback',
}
